import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../contexts/LanguageContext';

const { FiMonitor, FiTool, FiCheckCircle, FiClock, FiPhone, FiMessageCircle, FiAlertCircle } = FiIcons;

const RepairArticle = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);

    // SEO Meta Tags - Optimized for local areas
    const seoData = {
      es: {
        title: 'Reparación de PC La Manga del Mar Menor, Cartagena y San Javier | Servicio a Domicilio 24/7',
        description: 'Servicio profesional de reparación de PC y portátiles a domicilio en La Manga del Mar Menor, Cartagena y San Javier. Más de 20 años de experiencia. Diagnóstico gratuito. Disponible 24/7. ☎️ 642 643 499'
      },
      en: {
        title: 'PC Repair La Manga del Mar Menor, Cartagena & San Javier | 24/7 Home Service',
        description: 'Professional PC and laptop repair service at home in La Manga del Mar Menor, Cartagena and San Javier. Over 20 years of experience. Free diagnosis. Available 24/7. ☎️ 642 643 499'
      },
      it: {
        title: 'Riparazione PC La Manga del Mar Menor, Cartagena e San Javier | Servizio a Domicilio 24/7',
        description: 'Servizio professionale di riparazione PC e portatili a domicilio a La Manga del Mar Menor, Cartagena e San Javier. Oltre 20 anni di esperienza. Diagnosi gratuita. Disponibile 24/7. ☎️ 642 643 499'
      }
    };

    document.title = seoData[language].title;
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = seoData[language].description;

    // Open Graph tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.content = seoData[language].title;

    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.content = seoData[language].description;
  }, [language]);

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center space-x-2 bg-white/20 px-4 py-2 rounded-full mb-6">
              <SafeIcon icon={FiMonitor} className="w-5 h-5" />
              <span className="font-semibold">{t('articles.repair.category')}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {t('articles.repair.title')}
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              {t('articles.repair.subtitle')}
            </p>
            <div className="flex items-center justify-center space-x-6 text-sm">
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiClock} className="w-4 h-4" />
                <span>{t('articles.readTime')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiCheckCircle} className="w-4 h-4" />
                <span>{t('articles.updated')}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Banner */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8"
      >
        <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-blue-200">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <SafeIcon icon={FiAlertCircle} className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">{t('articles.cta.urgent')}</h3>
                <p className="text-sm text-gray-600">{t('articles.cta.available')}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.open('https://wa.me/34642643499', '_blank')}
                className="flex items-center space-x-2 bg-green-600 text-white px-6 py-3 rounded-lg font-semibold"
              >
                <SafeIcon icon={FiMessageCircle} className="w-5 h-5" />
                <span>WhatsApp</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.location.href = 'tel:+34642643499'}
                className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold"
              >
                <SafeIcon icon={FiPhone} className="w-5 h-5" />
                <span>{t('articles.cta.call')}</span>
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="prose prose-lg max-w-none"
        >
          <img
            src="https://images.unsplash.com/photo-1581092160562-40aa08e78837?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
            alt={t('articles.repair.title')}
            className="w-full h-96 object-cover rounded-2xl mb-8 shadow-lg"
          />

          <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              {t('articles.repair.intro.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('articles.repair.intro.p1')}
            </p>
            <p className="text-gray-700 leading-relaxed">
              {t('articles.repair.intro.p2')}
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              {t('articles.repair.problems.title')}
            </h2>
            <div className="space-y-4">
              {['problem1', 'problem2', 'problem3', 'problem4', 'problem5'].map((key, index) => (
                <div key={key} className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">
                      {t(`articles.repair.problems.${key}.title`)}
                    </h3>
                    <p className="text-gray-700">
                      {t(`articles.repair.problems.${key}.description`)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              {t('articles.repair.process.title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {['step1', 'step2', 'step3', 'step4'].map((key, index) => (
                <div key={key} className="bg-white rounded-xl p-6">
                  <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xl mb-4">
                    {index + 1}
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">
                    {t(`articles.repair.process.${key}.title`)}
                  </h3>
                  <p className="text-gray-700">
                    {t(`articles.repair.process.${key}.description`)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              {t('articles.repair.why.title')}
            </h2>
            <div className="space-y-4">
              {['reason1', 'reason2', 'reason3', 'reason4'].map((key) => (
                <div key={key} className="flex items-start space-x-3 bg-blue-50 p-4 rounded-lg">
                  <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <p className="text-gray-700">
                    {t(`articles.repair.why.${key}`)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Final CTA */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-2xl p-8 text-center">
            <h2 className="text-3xl font-bold mb-4">
              {t('articles.repair.finalCta.title')}
            </h2>
            <p className="text-xl mb-6 text-blue-100">
              {t('articles.repair.finalCta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.open('https://wa.me/34642643499?text=Hola%2C%20necesito%20reparaci%C3%B3n%20de%20PC%20en%20La%20Manga', '_blank')}
                className="flex items-center justify-center space-x-2 bg-green-600 text-white px-8 py-4 rounded-xl font-semibold"
              >
                <SafeIcon icon={FiMessageCircle} className="w-5 h-5" />
                <span>{t('articles.repair.finalCta.whatsapp')}</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/#contact')}
                className="flex items-center justify-center space-x-2 bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold"
              >
                <SafeIcon icon={FiTool} className="w-5 h-5" />
                <span>{t('articles.repair.finalCta.contact')}</span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </article>
    </div>
  );
};

export default RepairArticle;